import { supabase } from "@/integrations/supabase/client";
import type { Task, CreateTaskInput, UpdateTaskInput } from "@/hooks/useTasks";
import { publishTaskChange, withTaskWrite } from "./taskChanges";
import { acknowledgeTaskAction, executeReviewedTaskAction, invokeTaskAction, newTaskOperationId,
  parseTaskRecord, readPendingTaskAction, TaskExecutorError, taskOperationKey,
  type TaskRequest, type TaskRecord, type TaskSnapshot, type TaskWriteAction } from "./taskExecutor";
import { canonicalTaskRecord, canonicalTaskSnapshot, normalizeTaskDueDate, parseTaskActionRequest } from "../../supabase/functions/_shared/taskActionDomain";

export type { TaskSnapshot } from "./taskExecutor";
export type TaskWriteGuard = () => void;
export const TASK_SELECT_FIELDS = "id,user_id,title,priority,due_date,time_label,done,created_at,source,external_id,client_action_id,completed_at";
const LOCAL_SOURCES = new Set([null, "manual", "mirror", "voice"]);
export const isSyncedTask = (task: Pick<Task, "source">) => !LOCAL_SOURCES.has(task.source ?? null);
export function snapshotForTask(task: Task): TaskSnapshot | null {
  try {
    const values = task as unknown as Record<string, unknown>;
    const record = canonicalTaskRecord({ task: Object.fromEntries(TASK_SELECT_FIELDS.split(",").map(key => [key, values[key]])) }, task.user_id);
    return record.writable ? record.snapshot : null;
  } catch { return null; }
}
export async function taskUser(expectedUserId?: string): Promise<string> {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user || (expectedUserId && expectedUserId !== user.id)) throw error ?? new Error("TASK_SESSION_UNAVAILABLE");
  return user.id;
}

/** A transition away and back is still a different account generation. */
export async function withTaskOwner<T>(expected: string | undefined, guard: TaskWriteGuard | undefined,
  work: (uid: string, assert: TaskWriteGuard) => Promise<T>): Promise<T> {
  let owner: string | null | undefined = expected, generation = 0;
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    const next = session?.user.id ?? null;
    if (owner !== undefined && owner !== next) generation++;
    owner = next;
  });
  try {
    const epoch = generation;
    guard?.(); const uid = await taskUser(expected);
    if (owner === undefined) owner = uid;
    const assert = () => { guard?.(); if (generation !== epoch || owner !== uid) throw new Error("TASK_SESSION_UNAVAILABLE"); };
    assert(); return await work(uid, assert);
  } finally { subscription.unsubscribe(); }
}
async function freshTask(id: string, uid: string, assert: TaskWriteGuard, writable = true): Promise<TaskRecord> {
  const response = await invokeTaskAction({ action: "get", task_id: id }, uid, assert); assert();
  const task = parseTaskRecord(response.task);
  if (!task || task.id !== id || task.user_id !== uid || !task.snapshot) throw new TaskExecutorError("incomplete_task_snapshot");
  if (writable && (!task.writable || task.read_only || task.content_truncated)) throw new TaskExecutorError(task.write_restriction ?? "task_read_only");
  return task;
}
const intentFields = ["action", "task_id", "title", "priority", "due_date", "time_label", "done", "event_id", "source", "client_action_id"];
function intentKey(request: TaskRequest, omitId = false): string {
  const generatedKey = request.action === "create" && request.client_action_id === taskOperationKey(String(request.operation_id), "create");
  return JSON.stringify(Object.fromEntries(intentFields.filter(key => key in request && !(omitId && key === "task_id") && !(generatedKey && key === "client_action_id"))
    .sort().map(key => [key, key === "due_date" ? normalizeTaskDueDate(request.due_date) : (request as unknown as Record<string, unknown>)[key]])));
}
const attempts = new Map<string, TaskRequest>();
async function execute(uid: string, intent: TaskRequest & { action: TaskWriteAction; task_id: string }, assert: TaskWriteGuard, expectedDone?: boolean): Promise<Task | null> {
  const pending = await readPendingTaskAction(uid); assert();
  if (pending && (pending.body.mirror_context || intentKey(pending.body as unknown as TaskRequest) !== intentKey(intent))) throw new TaskExecutorError("task_pending_action");
  const key = uid + ":" + intentKey(intent);
  let request = (pending?.body as unknown as TaskRequest | undefined) ?? attempts.get(key);
  if (!request) {
    const reviewed = "expected_snapshot" in intent ? canonicalTaskSnapshot(intent.expected_snapshot, uid) : null;
    if (reviewed && !canonicalTaskRecord(reviewed, uid).writable) throw new TaskExecutorError("task_read_only");
    const previous = intent.action === "create" || reviewed ? null : await freshTask(intent.task_id, uid, assert);
    if (expectedDone !== undefined && (reviewed ?? previous?.snapshot)?.task.done !== expectedDone) throw new Error("TASK_STALE_TARGET");
    request = parseTaskActionRequest({ ...intent, operation_id: intent.operation_id ?? newTaskOperationId(), confirmed: true,
      ...(reviewed ? { expected_snapshot: reviewed } : previous ? { expected_snapshot: previous.snapshot } : {}) });
    attempts.set(key, request);
  }
  try {
    const { entry } = await executeReviewedTaskAction(uid, request, assert); assert();
    let task: Task | null = null;
    if (request.action === "delete") {
      const check = await supabase.from("tasks").select("id").eq("id", request.task_id!).eq("user_id", uid).maybeSingle(); assert();
      if (check.error || check.data) throw check.error ?? new TaskExecutorError("task_result_unconfirmed");
    } else task = await freshTask(request.task_id!, uid, assert, false) as unknown as Task;
    await taskUser(uid); assert();
    publishTaskChange({ userId: uid, id: request.task_id!, task });
    await acknowledgeTaskAction(entry, assert); assert();
    if (!["create", "delete"].includes(request.action)) attempts.delete(key);
    return task;
  } catch (cause) {
    if (cause instanceof TaskExecutorError && cause.safe_retry) attempts.delete(key);
    throw cause;
  }
}
/** Stable local identity for a reviewed legacy email/proactive creation key. */
async function clientTaskId(uid: string, key: string): Promise<string> {
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(["vyv-local-task-client", uid, key])))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 80; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
}
/** Preserve valid legacy keys; opaque provider IDs are hashed in full, never truncated. */
export async function taskClientKey(ownerId: string, raw: string, prefix: string): Promise<string> {
  if (/^[A-Za-z0-9._:+/=-]{1,160}$/.test(raw)) return raw;
  if (!/^[A-Za-z0-9_]{1,80}$/.test(prefix)) throw new Error("invalid_client_action_id");
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(["vyv-local-task-key", ownerId, raw]))));
  return prefix + "_" + Array.from(digest, byte => byte.toString(16).padStart(2, "0")).join("");
}
export function createConfirmedTask(input: CreateTaskInput, expectedUserId?: string, guard?: TaskWriteGuard): Promise<Task> {
  return withTaskOwner(expectedUserId, guard, async (uid, assert) => {
    const fields: TaskRequest = { action: "create", title: input.title.trim(), priority: input.priority, due_date: input.due_date ?? null,
      time_label: input.time_label ?? null, source: input.source ?? "manual", ...(input.client_action_id !== undefined ? { client_action_id: input.client_action_id } : {}) };
    const pending = await readPendingTaskAction(uid); assert();
    const recovered = pending && !pending.body.mirror_context && pending.body.action === "create" && intentKey(pending.body as unknown as TaskRequest, true) === intentKey(fields, true);
    const id = input.id ?? (input.client_action_id ? await clientTaskId(uid, input.client_action_id) : recovered ? String(pending.body.task_id) : newTaskOperationId()); assert();
    return withTaskWrite(id, async () => (await execute(uid, { ...fields, action: "create", task_id: id,
      ...(input.operation_id !== undefined ? { operation_id: input.operation_id } : {}) }, assert))!);
  });
}
export async function updateConfirmedTask(id: string, fields: UpdateTaskInput & { external_id?: string }, expectedUserId?: string,
  expectedDone?: boolean, expectedSnapshot?: TaskSnapshot, guard?: TaskWriteGuard): Promise<Task> {
  if (expectedSnapshot !== undefined) canonicalTaskSnapshot(expectedSnapshot, expectedUserId);
  return withTaskWrite(id, () => withTaskOwner(expectedUserId, guard, async (uid, assert) => {
    const reviewed = expectedSnapshot !== undefined ? canonicalTaskSnapshot(expectedSnapshot, uid) : undefined;
    const hasExternalId = Object.prototype.hasOwnProperty.call(fields, "external_id");
    if (hasExternalId && (Object.keys(fields).length !== 1 || typeof fields.external_id !== "string" || !fields.external_id)) throw new Error("TASK_INVALID_PATCH");
    const intent: TaskRequest = hasExternalId ? { action: "link_event", task_id: id, event_id: fields.external_id }
      : { action: "update", task_id: id, ...fields };
    if (!hasExternalId && Object.keys(fields).length === 1 && typeof fields.done === "boolean") {
      intent.action = fields.done ? "complete" : "reopen"; delete intent.done;
    }
    if (reviewed !== undefined) intent.expected_snapshot = reviewed;
    return (await execute(uid, intent as TaskRequest & { action: TaskWriteAction; task_id: string }, assert, expectedDone))!;
  }));
}
export async function deleteConfirmedTask(id: string, expectedUserId?: string, expectedSnapshot?: TaskSnapshot, guard?: TaskWriteGuard): Promise<void> {
  if (expectedSnapshot !== undefined) canonicalTaskSnapshot(expectedSnapshot, expectedUserId);
  return withTaskWrite(id, () => withTaskOwner(expectedUserId, guard, async (uid, assert) => {
    await execute(uid, { action: "delete", task_id: id, ...(expectedSnapshot !== undefined ? { expected_snapshot: expectedSnapshot } : {}) }, assert);
  }));
}
